import { addHours } from 'date-fns';
import * as _ from 'lodash';
import {
  createFlightHoursDto,
  createPilotLandingsDto,
  FlightHoursDto,
  FlightLogEntryDto,
  LogMode,
  LogStatus,
  PilotLandingsDto,
  WaypointDto,
} from 'src/app/shared/flight-log-api/flight-logs/flight-log-client.model';
import { FlightLogSummaryEntity } from 'src/store/flight-log-summary/flight-log-summary.model';
import { FlightLogCommonProps, FlightLogEntity } from 'src/store/flight-log/flight-log.model';
import { convertHoursToMilliseconds, isDate, parseDateOrDefault } from 'src/util/dateLib';
import { round, sum } from 'src/util/math';
import { sumValues } from 'src/util/object';
import { humanize, hyphenate } from 'src/util/text';
import { getOrElse } from 'src/util/values';

export interface CollectionResult<T> {
  results: T[];
  totalCount: number;
}
export interface FlightHourTotals extends FlightHoursDto {
  dayNightHoursSum: number;
  part91part135HoursSum: number;
  ifrHoursSum: number;
  dayNightHoursSumInMs: number;
  part91part135HoursSumInMs: number;
  ifrHoursSumInMs: number;
  part133Sum: number;
}

export function sumFlightHours(flightHours: FlightHoursDto[]): FlightHourTotals {
  const zeros = createFlightHoursDto();
  const flightHourSums = sumValues([zeros, ...flightHours]) as FlightHoursDto;
  const {
    day,
    nightAided,
    nightUnaided,
    part135,
    part91,
    ifrImc,
    ifrSim,
    part133HoistDayHours,
    part133HoistNightHours,
    part133HookDayHours,
    part133HookNightHours,
  } = flightHourSums;
  const dayNightHoursSum = round(sum(day, nightAided, nightUnaided), 2);
  const dayNightHoursSumInMs = convertHoursToMilliseconds(dayNightHoursSum);
  const part91part135HoursSum = round(sum(part91, part135), 2);
  const part91part135HoursSumInMs = convertHoursToMilliseconds(part91part135HoursSum);
  const ifrHoursSum = round(sum(ifrImc, ifrSim), 2);
  const ifrHoursSumInMs = convertHoursToMilliseconds(ifrHoursSum);
  const part133Sum = round(
    sum(part133HoistDayHours, part133HoistNightHours, part133HookDayHours, part133HookNightHours),
    2,
  );
  const totals = {
    ...flightHourSums,
    dayNightHoursSumInMs,
    dayNightHoursSum,
    part91part135HoursSum,
    part91part135HoursSumInMs,
    ifrHoursSum,
    ifrHoursSumInMs,
    part133Sum,
  };
  return totals;
}

export function getSumFlightHours(flightLog: FlightLogEntity): FlightHourTotals {
  const flightHours = _(flightLog.entries)
    .flatMap((e) => e.pilotLogEntries)
    .flatMap((p) => p.flightHours)
    .value();
  const totals = sumFlightHours(flightHours);

  return totals;
}

export interface LegDurationInfo {
  durationInMs: number;
}

export function getLegDurationInMs(
  flightLogEntry: FlightLogEntryDto,
  flightLog: FlightLogEntity,
): number | null {
  const { fromWaypointId, toWaypointId } = flightLogEntry.entryDetail;
  const from = fromWaypointId != null ? getWaypoint(flightLog, fromWaypointId) : null;
  const to = toWaypointId != null ? getWaypoint(flightLog, toWaypointId) : null;

  if (from != null && to != null && isDate(from.departureTime) && isDate(to.arrivalTime)) {
    // tslint:disable-next-line: no-non-null-assertion
    const durationInMs = to.arrivalTime!.getTime() - from.departureTime!.getTime();
    return durationInMs;
  }
  return null;
}

export function getFlightDurationInMs(flightLog: FlightLogEntity) {
  const legDurations = flightLog.entries.map((entry) => getLegDurationInMs(entry, flightLog));
  const flightDurationInMs = _.sum([0, ...legDurations]);
  return flightDurationInMs;
}

export function getSumFlightHoursForLogEntry(flightLogEntry: FlightLogEntryDto) {
  const flightHours = _(flightLogEntry.pilotLogEntries)
    .flatMap((e) => e.flightHours)
    .value();
  const totals = sumFlightHours(flightHours);
  return totals;
}

export interface LandingTotals extends PilotLandingsDto {
  dayNightSum: number;
  hnvgo: number;
}

export function getSumPilotLandings(flightLog: FlightLogEntity): LandingTotals {
  const landings = _(flightLog.entries)
    .flatMap((e) => e.pilotLogEntries)
    .flatMap((p) => ({ ...p.landings, hnvgo: p.hnvgo }))
    .value();
  const sums = sumValues([{ ...createPilotLandingsDto(), hnvgo: 0 }, ...landings]) as LandingTotals;
  const totals = { ...sums, dayNightSum: sum(sums.day, sums.night) };
  return totals;
}

export function getTotalFlightTime(flightLog: FlightLogCommonProps) {
  const ms = getMaintenanceSummary(flightLog);
  return ms != null ? ms.totalFlightTime : null;
}

export function getWaypoints(
  flightLogEntry: FlightLogEntryDto,
  flightLog: FlightLogEntity,
): {
  fromWaypoint: WaypointDto | null;
  toWaypoint: WaypointDto | null;
} {
  const { fromWaypointId, toWaypointId } = flightLogEntry.entryDetail;
  const fromWaypoint = fromWaypointId != null ? getWaypoint(flightLog, fromWaypointId) : null;
  const toWaypoint = toWaypointId != null ? getWaypoint(flightLog, toWaypointId) : null;
  return { fromWaypoint, toWaypoint };
}

export function getWaypoint(flightLog: FlightLogEntity, waypointId: string): WaypointDto | null {
  // tslint:disable-next-line: no-non-null-assertion
  return flightLog.waypoints.find((waypoint) => waypoint.id === waypointId)!;
}

export function getTotalLandings(flightLog: FlightLogEntity) {
  const ms = getMaintenanceSummary(flightLog);
  return ms != null ? ms.totalLandings : null;
}

export function getFlightStartEndTimesOrDefault(
  flightLog: FlightLogSummaryEntity | FlightLogEntity,
):
  | {
      startTime: Date;
      endTime: Date;
    }
  | undefined {
  /*

TODO: this code needs to be cleaned up - it was written before proper knowledge that
either a FlightLogSummary OR FlightLogEntity could be passed as a parameter. This may
need to be extracted into two different functions or added to the view models


    */

  if (flightLog.mode === 'Simple') {
    return undefined;
  }
  const startTime =
    flightLog.mode === 'EntryPerLeg'
      ? getOrElse(
          _((flightLog as any).waypoints)
            .map((w) => w.departureTime)
            .filter((dt) => dt != null)
            .min(),
          parseDateOrDefault((flightLog as any).dateOfService),
        )
      : parseDateOrDefault(
          (flightLog as any).entries != null ? (flightLog as any).entries[0].flightTime : null,
        );

  const totalFlightTime = getTotalFlightTime(flightLog);
  const latestArrivalTime = _((flightLog as any).waypoints)
    .map((w) => w.arrivalTime)
    .filter((dt) => dt != null)
    .max();

  const endTime =
    latestArrivalTime != null
      ? latestArrivalTime
      : startTime != null && totalFlightTime != null
      ? addHours(startTime, totalFlightTime)
      : undefined;

  return startTime != null && endTime != null ? { startTime, endTime } : undefined;
}

export function getMaintenanceSummary(flightLog: FlightLogCommonProps) {
  return flightLog != null && flightLog.maintenanceSummary != null
    ? flightLog.maintenanceSummary
    : null;
}

export function logStatusDisplay(status: LogStatus): string {
  switch (status) {
    case 'NotStarted':
      return humanize(status);
    case 'ReOpened':
      return hyphenate(status);
    default:
      return status;
  }
}

export function externalOr(logMode: LogMode, value: any, defaultValue: any = 'Unknown'): any {
  return logMode === 'Simple' ? 'External' : getOrElse(value, defaultValue);
}

export function logModeDisplay(mode: LogMode): string {
  return humanize(mode);
}
